import type Firebase from 'firebase/remote-config'
import { fetchAndActivate, getRemoteConfig, getValue as getFirebaseValue } from 'firebase/remote-config'
import { store } from '../../app/store'
import env from '../../env.json'
import { firebase } from '../../firebase'
import { getFlagOverrides } from '@/features/devTools/flagOverrides'
import { defaultFlags, type Flags } from './defaultFlags'
import { SET_FLAGS } from './FlagsConstants'

const remoteConfig = getRemoteConfig(firebase)
// A floor on *triggered* Remote Config fetches, not a poll interval — nothing here polls,
// so fetch volume is bounded by navigation, not by this number. 60s exists so a flag flipped
// after a release shows up on the next reload instead of up to an hour later. It sits inside
// the 15–60s band the flag-service ecosystem settled on (Unleash 15s, LaunchDarkly 30s
// polling, ConfigCat 60s); Firebase's 12h default targets battery-bound mobile, not web.
const PRODUCTION_FETCH_INTERVAL_MS = 60_000
const NON_PRODUCTION_FETCH_INTERVAL_MS = 0

remoteConfig.settings.minimumFetchIntervalMillis =
  env.name === 'Production' ? PRODUCTION_FETCH_INTERVAL_MS : NON_PRODUCTION_FETCH_INTERVAL_MS

function setFlags(payload: Flags) {
  const action = {
    type: SET_FLAGS,
    payload,
  }
  return action
}

const asBoolean = (value: Firebase.Value) => value.asBoolean()
const asJSON = (value: Firebase.Value) => {
  try {
    return JSON.parse(value.asString())
  } catch (error) {
    console.error('Error parsing flag', error)
    return value.asString()
  }
}
// Keyed to `Flags` so every flag is required to have a parser. Without one a flag fell through
// to the raw string, and `"false"` is truthy — `id_verification_show_tag_admin` was doing
// exactly that, so the tag would have shown precisely when the flag was turned off.
const flagParsers: Record<keyof Flags, (value: Firebase.Value) => unknown> = {
  id_verification_show_tag_admin: asBoolean,
  id_verification_show_tag_provider: asBoolean,
  product_order_types: asJSON,
  dts_moud_pilot: asJSON,
  merge_duplicate_accounts: asJSON,
  pdmp_pilot: asJSON,
  pdmp_integrated_states: asJSON,
  adyen: asJSON,
  admin_google_sso: asBoolean,
  pharmacy_search_daw: asBoolean,
  program_visit_badge_admin: asBoolean,
}

const flagPrefix = {
  DEV: 'dev_',
  LOCAL: 'dev_',
  legolas: 'qa_',
  gimli: 'qa_',
  boromir: 'qa_',
  pippin: 'qa_',
  merry: 'qa_',
  QA: 'qa_',
  UAT: 'qa_',
  Autotest: 'qa_',
  PreProd: 'preprod_',
  Production: 'prod_',
}

// `env.name` is a plain `string` off env.json, so a keyed lookup can never narrow to one of
// these literals — the result typed as `void` and needed a lint suppression to sit inside the
// template. Narrowing first keeps the fallback and drops both.
//
// `hasOwnProperty.call` rather than `in`, which also answers true for everything on
// `Object.prototype` — `'toString'` would satisfy the predicate and then index to a function.
// `Object.hasOwn` reads better but is an ES2022 *built-in*, which the bundler's target only
// transpiles syntax for, so it would throw on the older Safari the browserslist still covers.
const isKnownEnv = (name: string): name is keyof typeof flagPrefix =>
  Object.prototype.hasOwnProperty.call(flagPrefix, name)

const flagName = (key: string) => `${isKnownEnv(env.name) ? flagPrefix[env.name] : flagPrefix.QA}${key}`

export function setConfigFlags(): void {
  // Seeded from the code defaults so the result is a complete `Flags` by construction.
  const flags: Flags = { ...defaultFlags }

  Object.keys(defaultFlags).forEach((key) => {
    const parsed = flagParsers[key](getFirebaseValue(remoteConfig, flagName(key)))
    // Each parser returns the type its own flag declares, but `ts-reset` types `JSON.parse` as
    // `unknown`, so that pairing cannot be stated to the compiler. Assigned through
    // `Object.assign` rather than an assertion, which this repo's lint config forbids.
    Object.assign(flags, { [key]: parsed })
  })

  // Dev Tools pins are applied last so they outrank Remote Config — a flag pinned from the panel
  // would otherwise be silently undone by the next fetch, which is the point of pinning it.
  // Always empty in production, where the override layer is inert.
  Object.assign(flags, getFlagOverrides())

  store.dispatch(setFlags(flags))
}

/**
 * Applies Dev Tools pins to the flags already in the store, without re-reading Remote Config.
 *
 * The panel needs the toggle to land immediately; `setConfigFlags` only runs on a fetch, which
 * could be a long way off. Both paths merge the same override set on top, so the two agree.
 */
export function applyFlagOverrides(overrides: Partial<Flags>): void {
  store.dispatch(setFlags({ ...store.getState().flags.flags, ...overrides }))
}

export async function getFlags(): Promise<void> {
  // Keyed by the *prefixed* name, because that is what `setConfigFlags` reads. Unprefixed,
  // every default was dead: a param missing from the template resolved to the type's zero
  // value instead of the intended default, which is how `admin_google_sso` — a `true`
  // default — could come back false.
  remoteConfig.defaultConfig = Object.fromEntries(
    Object.entries(defaultFlags).map(([key, value]) => [
      flagName(key),
      typeof value === 'string' ? value : JSON.stringify(value),
    ]),
  )
  // Awaited rather than left as a floating `.then`, so a caller can observe the failure —
  // `getFlags` already advertises a promise, and a rejected fetch used to surface only as an
  // unhandled rejection. Removes this file's `no-floating-promises` and `require-await`
  // suppressions.
  await fetchAndActivate(remoteConfig)

  setConfigFlags()
}
